import { AbsenceDatePicker } from '@lmt/unicorn-shared/app/components/absences/AbsenceDatePicker';
import { AbsenceCalendarType } from '@lmt/unicorn-shared/app/components/absences/common/types';
import { AbsenceMonthCalendar } from '@lmt/unicorn-shared/app/components/absences/AbsenceMonthCalendar';
import { AbsenceYearCalendar } from '@lmt/unicorn-shared/app/components/absences/AbsenceYearCalendar';
import { CalendarTypeDictionary } from '@lmt/unicorn-shared/app/components/calendar/configuration/common/types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import PlusIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import React, { FC, useMemo } from 'react';
import { DateParam, NumberParam, withDefault } from 'react-router-url-params';
import styles from './AbsencesAndHolidaysPage.module.scss';
import { useEventCallback } from '@mui/material';

const calendarTypesKeys = Object.keys(AbsenceCalendarType);

export const AbsencesAndHolidaysPage: FC = () => {
  const i18next = useScopedTranslation('absences');
  const forms = useGlobalForms();
  const claims = useClaims();

  const [queryParams, setQueryParams] = useQueryParamsInTab({
    calendarType: withDefault(NumberParam, AbsenceCalendarType.Year),
    currentDate: withDefault(DateParam, new Date()),
  });

  const setCurrentDate = useEventCallback((date: Date) => {
    setQueryParams({
      currentDate: date,
    });
  });

  const calendarTypes: CalendarTypeDictionary[] = useMemo(
    () =>
      calendarTypesKeys
        .filter((key) => !Number.isNaN(Number(key)))
        .map((key) => ({
          key: Number(key),
          value: i18next.t(
            `calendar_types.${AbsenceCalendarType[
              Number(key)
            ].toLowerCase()}` as any,
          ),
        })),
    [calendarTypesKeys],
  );

  return (
    <div data-test-id="absence-and-holidays-page">
      <div className={styles.header}>
        <div className={styles.headerControls}>
          <span className={styles.title}>{i18next.t('title')}</span>
          <AbsenceDatePicker
            currentDate={queryParams.currentDate}
            setCurrentDate={setCurrentDate}
            calendarType={queryParams.calendarType}
          />
        </div>
        <div className={styles.headerControls}>
          <DropDownInput
            useIdFunctionAsValue={true}
            rootClassName={styles.calendarTypeDropdown}
            options={calendarTypes}
            value={queryParams.calendarType}
            idFunction={'key'}
            getOptionLabel={'value'}
            required={true}
            onValueChanged={(value) => {
              setQueryParams({ calendarType: value?.key });
            }}
          ></DropDownInput>
          {claims.permissions.manageAbsences && (
            <Button
              color={ButtonColor.Primary}
              iconComponent={<PlusIcon />}
              title={i18next.t('add_button_title')}
              className={styles.addButton}
              onClick={() => forms.openAbsenceCreating()}
            />
          )}
        </div>
      </div>
      <div className={styles.calendarContainer}>
        {queryParams.calendarType === AbsenceCalendarType.Year && (
          <AbsenceYearCalendar currentDate={queryParams.currentDate} />
        )}
        {queryParams.calendarType === AbsenceCalendarType.Month && (
          <AbsenceMonthCalendar currentDate={queryParams.currentDate} />
        )}
      </div>
    </div>
  );
};
